import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineKey,
  HiOutlineCalendar,
  HiOutlineCheckCircle,
  HiOutlineClock,
} from "react-icons/hi2";
import { FaUsers, FaBuilding, FaCity, FaUserTie } from "react-icons/fa";
import {
  useGetAdminAssessmentCapacityUtilizationQuery,
  useGetAdminAssessmentExportQuery,
  useGetAdminAssessmentSalesQuery,
  useGetAdminAssessmentTechnologicSalesQuery,
  useGetAdminAverageOhiQuery,
  useGetAdminDashboardOverviewQuery,
} from "reduxFiles/api/reportAndFormApi";
import IconButton from "components/button/IconButton";
import { IoMdRefresh } from "react-icons/io";
import AdminRankingOhi from "./AdminRankingOhi";
import { useState, useMemo, useCallback } from "react";
import DateRangeFilter from "./DateRangeFilter";
import AdminChart from "./AdminChart";
import { useGetIndustriesQuery } from "reduxFiles/api/companyApi";
import { useYearRangeDates } from "hooks/useYearRangeDates";
import { useScope } from "hooks/useScope";

const AdminDashboard = () => {
  const { t } = useTranslation();
  const scope = useScope();
  const { defaultRange } = useYearRangeDates();
  const { fromDate: defaultFromISO, toDate: defaultToISO } = defaultRange;

  const { data, isLoading, refetch } = useGetAdminDashboardOverviewQuery(scope);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const { data: sectorsData } = useGetIndustriesQuery();

  const sectorOptions = useMemo(() => {
    if (!sectorsData) return [];
    return sectorsData.map((sector: any) => ({
      id: sector.id,
      name: sector.name,
      key: sector.key || String(sector.id),
    }));
  }, [sectorsData]);

  const mainStats = useMemo(
    () => [
      {
        label: t("dashboard.totalUsers"),
        value: data?.users || 0,
        icon: <FaUsers className="h-6 w-6" />,
        color: "from-blue-500 to-blue-600",
        bgColor: "bg-blue-50 dark:bg-blue-500/20",
        textColor: "text-blue-600 dark:text-blue-400",
        description: t("dashboard.totalUsersDesc"),
      },
      {
        label: t("dashboard.totalOrganizations"),
        value: data?.organizations || 0,
        icon: <FaCity className="h-6 w-6" />,
        color: "from-purple-500 to-purple-600",
        bgColor: "bg-purple-50 dark:bg-purple-500/20",
        textColor: "text-purple-600 dark:text-purple-400",
        description: t("dashboard.totalOrganizationsDesc"),
      },
      {
        label: t("dashboard.totalCompanies"),
        value: data?.companies || 0,
        icon: <FaBuilding className="h-6 w-6" />,
        color: "from-green-500 to-green-600",
        bgColor: "bg-green-50 dark:bg-green-500/20",
        textColor: "text-green-600 dark:text-green-400",
        description: t("dashboard.totalCompaniesDesc"),
      },
      {
        label: t("dashboard.totalResponders"),
        value: data?.responder?.total || 0,
        icon: <FaUserTie className="h-6 w-6" />,
        color: "from-amber-500 to-amber-600",
        bgColor: "bg-amber-50 dark:bg-amber-500/20",
        textColor: "text-amber-600 dark:text-amber-400",
        description: t("dashboard.totalRespondersDesc"),
      },
    ],
    [data, t]
  );

  const licenseStats = useMemo(
    () => ({
      label: t("dashboard.licenseStats"),
      total: data?.license?.total || 0,
      used: data?.license?.used || 0,
      unused: data?.license?.unused || 0,
      usedPercentage: data?.license?.total
        ? Math.round((data.license.used / data.license.total) * 100)
        : 0,
      icon: <HiOutlineKey className="h-6 w-6" />,
    }),
    [data, t]
  );

  const assessmentStats = useMemo(
    () => ({
      label: t("dashboard.assessmentStats"),
      total: data?.assessment?.total || 0,
      completed: data?.assessment?.completed || 0,
      pending: data?.assessment?.pending || 0,
      completionRate: data?.assessment?.total
        ? Math.round((data.assessment.completed / data.assessment.total) * 100)
        : 0,
      icon: <HiOutlineClipboardDocumentList className="h-6 w-6" />,
    }),
    [data, t]
  );

  const [salesFilters, setSalesFilters] = useState({
    fromDate: defaultFromISO,
    toDate: defaultToISO,
    sectorId: null as string | null,
  });
  const [exportFilters, setExportFilters] = useState({
    fromDate: defaultFromISO,
    toDate: defaultToISO,
    sectorId: null as string | null,
  });
  const [capacityFilters, setCapacityFilters] = useState({
    fromDate: defaultFromISO,
    toDate: defaultToISO,
    sectorId: null as string | null,
  });
  const [ohiFilters, setOhiFilters] = useState({
    fromDate: defaultFromISO,
    toDate: defaultToISO,
    sectorId: null as string | null,
  });

  const {
    data: ohiData,
    isLoading: ohiLoading,
    refetch: refetchOhi,
  } = useGetAdminAverageOhiQuery({
    fromDate: ohiFilters.fromDate,
    toDate: ohiFilters.toDate,
    scope,
  });

  const {
    data: salesData,
    isLoading: salesLoading,
    refetch: refetchSales,
  } = useGetAdminAssessmentSalesQuery({
    fromDate: salesFilters.fromDate,
    toDate: salesFilters.toDate,
    scope,
  });

  const {
    data: technologicSalesData,
    isLoading: technologicSalesLoading,
    refetch: refetchTechnologicSales,
  } = useGetAdminAssessmentTechnologicSalesQuery({
    fromDate: salesFilters.fromDate,
    toDate: salesFilters.toDate,
    scope,
  });

  const refetchSalesCharts = useCallback(() => {
    refetchSales();
    refetchTechnologicSales();
  }, [refetchSales, refetchTechnologicSales]);

  const {
    data: exportData,
    isLoading: exportLoading,
    refetch: refetchExport,
  } = useGetAdminAssessmentExportQuery({
    fromDate: exportFilters.fromDate,
    toDate: exportFilters.toDate,
    scope,
  });

  const {
    data: capacityData,
    isLoading: capacityLoading,
    refetch: refetchCapacity,
  } = useGetAdminAssessmentCapacityUtilizationQuery({
    fromDate: capacityFilters.fromDate,
    toDate: capacityFilters.toDate,
    scope,
  });

  // Handler های بهینه شده برای فیلترها
  const handleOhiFromYearChange = useCallback((date: string) => {
    setOhiFilters((prev) => ({ ...prev, fromDate: date }));
  }, []);

  const handleOhiToYearChange = useCallback((date: string) => {
    setOhiFilters((prev) => ({ ...prev, toDate: date }));
  }, []);

  const handleOhiSectorChange = useCallback((sectorId: string | null) => {
    setOhiFilters((prev) => ({ ...prev, sectorId }));
  }, []);

  const handleSalesFromYearChange = useCallback((date: string) => {
    setSalesFilters((prev) => ({ ...prev, fromDate: date }));
  }, []);

  const handleSalesToYearChange = useCallback((date: string) => {
    setSalesFilters((prev) => ({ ...prev, toDate: date }));
  }, []);

  const handleSalesSectorChange = useCallback((sectorId: string | null) => {
    setSalesFilters((prev) => ({ ...prev, sectorId }));
  }, []);

  const handleCapacityFromYearChange = useCallback((date: string) => {
    setCapacityFilters((prev) => ({ ...prev, fromDate: date }));
  }, []);

  const handleCapacityToYearChange = useCallback((date: string) => {
    setCapacityFilters((prev) => ({ ...prev, toDate: date }));
  }, []);

  const handleCapacitySectorChange = useCallback((sectorId: string | null) => {
    setCapacityFilters((prev) => ({ ...prev, sectorId }));
  }, []);

  const handleExportFromYearChange = useCallback((date: string) => {
    setExportFilters((prev) => ({ ...prev, fromDate: date }));
  }, []);

  const handleExportToYearChange = useCallback((date: string) => {
    setExportFilters((prev) => ({ ...prev, toDate: date }));
  }, []);

  const handleExportSectorChange = useCallback((sectorId: string | null) => {
    setExportFilters((prev) => ({ ...prev, sectorId }));
  }, []);

  return (
    <div className="from-slate-50 min-h-screen bg-gradient-to-br via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="p-4 lg:p-6">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center"
          >
            <div className="flex items-center gap-4">
              <IconButton
                handler={handleRefresh}
                title={t("buttons.refresh")}
                icon={<IoMdRefresh size={22} />}
                className="bg-white shadow-lg hover:shadow-xl dark:bg-gray-800"
              />
              <div>
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                  {t("dashboard.overview")}
                </h2>
                <p className="text-sm text-gray-700 dark:text-gray-400">
                  {t("dashboard.overviewDescription")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-xl bg-white/80 px-4 py-2 shadow-lg backdrop-blur-sm dark:bg-gray-800/80">
                <HiOutlineCalendar className="h-4 w-4 text-gray-700 dark:text-gray-400" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {t("dashboard.lastUpdate")}
                </span>
                <span className="text-xs text-gray-700 dark:text-gray-400">
                  {new Date().toLocaleTimeString("fa-IR")}
                </span>
              </div>
            </div>
          </motion.div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-4">
                <div className="loader w-6 text-brand-500" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("dashboard.loading")}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {mainStats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      type: "spring",
                      damping: 20,
                      stiffness: 300,
                      delay: index * 0.1,
                    }}
                    whileHover={{
                      y: -6,
                      scale: 1.02,
                      transition: {
                        type: "spring",
                        damping: 15,
                        stiffness: 400,
                      },
                    }}
                    className="group relative overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-300 hover:shadow-xl dark:bg-gray-800/90"
                  >
                    <div className={`h-1 bg-gradient-to-r ${stat.color}`} />
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-400">
                            {stat.label}
                          </p>
                          <motion.p
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{
                              delay: index * 0.1 + 0.2,
                              type: "spring",
                            }}
                            className="mt-2 text-3xl font-bold text-gray-900 dark:text-white"
                          >
                            {stat.value.toLocaleString()}
                          </motion.p>
                          <p className="mt-1 text-xs text-gray-700 dark:text-gray-500">
                            {stat.description}
                          </p>
                        </div>
                        <div
                          className={`rounded-2xl p-3 transition-all duration-300 group-hover:rotate-3 group-hover:scale-110 ${stat.bgColor}`}
                        >
                          <div className={stat.textColor}>{stat.icon}</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="my-6 grid gap-5 lg:grid-cols-2">
                {/* License Stats Card */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    type: "spring",
                    damping: 20,
                    stiffness: 300,
                    delay: 0.3,
                  }}
                  whileHover={{
                    y: -6,
                    transition: {
                      type: "spring",
                      damping: 15,
                      stiffness: 400,
                    },
                  }}
                  className="dark:to-indigo-950/30 group relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/50 shadow-lg transition-all duration-300 hover:shadow-xl dark:border-indigo-900/30 dark:from-gray-800"
                >
                  <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-indigo-100/30 blur-2xl dark:bg-indigo-500/10" />
                  <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-purple-100/30 blur-2xl dark:bg-purple-500/10" />

                  <div className="relative p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 p-2.5 text-white shadow-lg shadow-indigo-500/25">
                          {licenseStats.icon}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            {licenseStats.label}
                          </p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {licenseStats.total.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-400">
                          {t("dashboard.usage")}
                        </p>
                        <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                          {licenseStats.usedPercentage}%
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-gray-700 dark:text-gray-400">
                        <span>
                          {t("dashboard.used")}: {licenseStats.used}
                        </span>
                        <span>
                          {t("dashboard.unused")}: {licenseStats.unused}
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${licenseStats.usedPercentage}%` }}
                          transition={{ delay: 0.6, duration: 0.8 }}
                          className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-600"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-4 text-xs text-gray-700 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <HiOutlineCheckCircle className="h-3.5 w-3.5 text-green-500" />
                        {licenseStats.used} {t("dashboard.activeLicenses")}
                      </span>
                      <span className="flex items-center gap-1">
                        <HiOutlineClock className="h-3.5 w-3.5 text-gray-400" />
                        {licenseStats.unused} {t("dashboard.availableLicenses")}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Assessment Stats Card */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    type: "spring",
                    damping: 20,
                    stiffness: 300,
                    delay: 0.4,
                  }}
                  whileHover={{
                    y: -6,
                    transition: {
                      type: "spring",
                      damping: 15,
                      stiffness: 400,
                    },
                  }}
                  className="dark:to-cyan-950/30 group relative overflow-hidden rounded-2xl border border-cyan-100 bg-gradient-to-br from-white to-cyan-50/50 shadow-lg transition-all duration-300 hover:shadow-xl dark:border-cyan-900/30 dark:from-gray-800"
                >
                  <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-cyan-100/30 blur-2xl dark:bg-cyan-500/10" />
                  <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-blue-100/30 blur-2xl dark:bg-blue-500/10" />

                  <div className="relative p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 p-2.5 text-white shadow-lg shadow-cyan-500/25">
                          {assessmentStats.icon}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            {assessmentStats.label}
                          </p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {assessmentStats.total.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-400">
                          {t("dashboard.completion")}
                        </p>
                        <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                          {assessmentStats.completionRate}%
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-gray-700 dark:text-gray-400">
                        <span>
                          {t("dashboard.completed")}:{" "}
                          {assessmentStats.completed}
                        </span>
                        <span>
                          {t("dashboard.pending")}: {assessmentStats.pending}
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{
                            width: `${assessmentStats.completionRate}%`,
                          }}
                          transition={{ delay: 0.7, duration: 0.8 }}
                          className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-4 text-xs text-gray-700 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <HiOutlineCheckCircle className="h-3.5 w-3.5 text-green-500" />
                        {assessmentStats.completed}{" "}
                        {t("dashboard.completedAssessments")}
                      </span>
                      <span className="flex items-center gap-1">
                        <HiOutlineClock className="h-3.5 w-3.5 text-yellow-500" />
                        {assessmentStats.pending}{" "}
                        {t("dashboard.pendingAssessments")}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="space-y-6">
                {/* OHI Chart */}
                <div>
                  <DateRangeFilter
                    fromDate={ohiFilters.fromDate}
                    toDate={ohiFilters.toDate}
                    onFromYearChange={handleOhiFromYearChange}
                    onToYearChange={handleOhiToYearChange}
                    onApply={refetchOhi}
                    className="mb-4"
                    sectors={sectorOptions}
                    selectedSector={ohiFilters.sectorId}
                    onSectorChange={handleOhiSectorChange}
                    showSectorFilter={true}
                  />
                  <AdminChart
                    data={ohiData}
                    title={t("dashboard.charts.ohiAverage")}
                    description={t("dashboard.charts.ohiAverageDescription")}
                    isLoading={ohiLoading}
                    onRefetch={refetchOhi}
                    type="ohi"
                    fromDate={ohiFilters.fromDate}
                    toDate={ohiFilters.toDate}
                    selectedSectorId={ohiFilters.sectorId || "all"}
                  />
                </div>

                <div>
                  <AdminRankingOhi />
                </div>

                {/* Sales Chart */}
                <div>
                  <DateRangeFilter
                    fromDate={salesFilters.fromDate}
                    toDate={salesFilters.toDate}
                    onFromYearChange={handleSalesFromYearChange}
                    onToYearChange={handleSalesToYearChange}
                    onApply={refetchSalesCharts}
                    className="mb-4"
                    sectors={sectorOptions}
                    selectedSector={salesFilters.sectorId}
                    onSectorChange={handleSalesSectorChange}
                    showSectorFilter={true}
                  />
                  <AdminChart
                    data={salesData}
                    technologicSalesData={technologicSalesData}
                    title={t("dashboard.charts.sales")}
                    description={t("dashboard.charts.salesDescription")}
                    isLoading={salesLoading || technologicSalesLoading}
                    onRefetch={refetchSalesCharts}
                    type="sales"
                    fromDate={salesFilters.fromDate}
                    toDate={salesFilters.toDate}
                    selectedSectorId={salesFilters.sectorId || "all"}
                  />
                </div>

                {/* Capacity Chart */}
                <div>
                  <DateRangeFilter
                    fromDate={capacityFilters.fromDate}
                    toDate={capacityFilters.toDate}
                    onFromYearChange={handleCapacityFromYearChange}
                    onToYearChange={handleCapacityToYearChange}
                    onApply={refetchCapacity}
                    className="mb-4"
                    sectors={sectorOptions}
                    selectedSector={capacityFilters.sectorId}
                    onSectorChange={handleCapacitySectorChange}
                    showSectorFilter={true}
                  />
                  <AdminChart
                    data={capacityData}
                    title={t("dashboard.charts.capacityUtilization")}
                    description={t(
                      "dashboard.charts.capacityUtilizationDescription"
                    )}
                    isLoading={capacityLoading}
                    onRefetch={refetchCapacity}
                    type="capacity"
                    fromDate={capacityFilters.fromDate}
                    toDate={capacityFilters.toDate}
                    selectedSectorId={capacityFilters.sectorId || "all"}
                  />
                </div>

                {/* Export Chart */}
                <div>
                  <DateRangeFilter
                    fromDate={exportFilters.fromDate}
                    toDate={exportFilters.toDate}
                    onFromYearChange={handleExportFromYearChange}
                    onToYearChange={handleExportToYearChange}
                    onApply={refetchExport}
                    className="mb-4"
                    sectors={sectorOptions}
                    selectedSector={exportFilters.sectorId}
                    onSectorChange={handleExportSectorChange}
                    showSectorFilter={true}
                  />
                  <AdminChart
                    data={exportData}
                    title={t("dashboard.charts.export")}
                    description={t("dashboard.charts.exportDescription")}
                    isLoading={exportLoading}
                    onRefetch={refetchExport}
                    type="export"
                    fromDate={exportFilters.fromDate}
                    toDate={exportFilters.toDate}
                    selectedSectorId={exportFilters.sectorId || "all"}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
